"use client";

import { cn, Table, useSort } from "@kyan/ui";
import { AnimatePresence, motion } from "motion/react";

import { EmptyState } from "#/components/Contents/components/EmptyState/EmptyState";
import { useAutoExpandRows } from "#/hooks/useAutoExpandRows";
import { usePositionsDrawerMarket } from "#/store/app/positionsDrawer/positionsDrawerMarket";
import { useOpenOrders } from "#/store/wallet/account/openOrders";
import { type OpenOrder } from "#/types/orders";

import { OptionOpenOrders } from "../OptionOpenOrders/OptionOpenOrders";
import {
  defaultSort,
  outerColumns,
} from "../OptionOpenOrders/OptionOpenOrders.constants";
import { PerpOpenOrders } from "../PerpOpenOrders/PerpOpenOrders";
import { getAllOpenOrderRows } from "./AllOpenOrders.utils";

export interface AllOpenOrdersProps {
  isPortfolioVariant?: boolean;
}

export const AllOpenOrders: React.FC<AllOpenOrdersProps> = ({
  isPortfolioVariant,
}) => {
  const { sort, setSort } = useSort<OpenOrder>({
    defaultSort,
  });
  const { market } = usePositionsDrawerMarket();
  const { openOrders, perpOpenOrders } = useOpenOrders();

  const allOpenOrders = getAllOpenOrderRows(openOrders);

  const isEmpty = !allOpenOrders.length;

  const { openRowIds, onRowClick } = useAutoExpandRows<OpenOrder>({
    dataLength: openOrders.length,
  });

  return (
    <Table
      isServerSide
      sort={sort}
      data={allOpenOrders}
      columns={outerColumns}
      openRowIds={openRowIds}
      defaultSort={defaultSort}
      emptyState={
        <EmptyState
          underlying={market.underlying}
          description={`Your open ${market.underlying} limit orders will show here`}
        />
      }
      classes={{
        wrapper: cn("bg-background flex flex-col", {
          "flex-1": !isEmpty,
          "h-full": isEmpty,
        }),
        table: cn("flex w-fit flex-col", {
          "flex-1 overflow-hidden": !isEmpty,
          "h-full": isEmpty,
          "border-b-0": isEmpty && !isPortfolioVariant,
        }),
        thead: "sticky top-0 bg-buttonBg border-b border-divider",
        columnClassName: "py-2.25 items-center",
        tbody: cn("text-textPrimary overflow-y-auto", {
          "flex-1": isEmpty,
          "border-divider border-b": !isEmpty && !isPortfolioVariant,
        }),
        row: "bg-buttonHover first:border-t-0 cursor-pointer",
        loading: {
          wrapper: "first:border-t-0",
        },
      }}
      onSortChange={setSort}
      onRowClick={onRowClick}
      renderRowExpansion={({ isOpen, row: { instrument } }) => {
        const isOptions = instrument.includes("Options");
        const isPerps = instrument.includes("Perps");

        return (
          <AnimatePresence>
            {isOpen && (
              <motion.div
                exit={{ height: 0 }}
                initial={{ height: 0 }}
                className="overflow-hidden"
                transition={{ duration: 0.3 }}
                animate={{
                  height: isPerps ? perpOpenOrders.length * 32 : "auto",
                }}
              >
                {isOptions && (
                  <OptionOpenOrders
                    isAllVariant
                    sort={sort}
                    onSortChange={setSort}
                  />
                )}

                {isPerps && <PerpOpenOrders isAllVariant />}
              </motion.div>
            )}
          </AnimatePresence>
        );
      }}
    />
  );
};
